// Small shared building blocks so every screen looks like the same product.
import { cloneElement, isValidElement, useEffect, useId, useState } from "react";
import { Link } from "@inertiajs/react";
import { ArrowLeftIcon } from "./icons";
import { formatMoney } from "./numbers";

// The shape every form in the portal is laid out on. Written once here because it was
// written nine times before — the same three strings copied into each *Fields file, which
// is how a customer form and a supplier form end up a column apart from each other without
// anybody deciding they should be.
//
// FORM_GRID is a card's fields: three across on a desktop, two on a tablet, one on a phone.
// A field that needs the whole width says so with `sm:col-span-2 lg:col-span-3`.
//
// FORM_ROW is the two-card row a form ends on — what is being entered on the left, what it
// comes to on the right. FORM_MAIN and FORM_ASIDE are its two halves, 3:2 rather than even
// because the left card holds fields and the right holds figures, and figures need less.
export const FORM_GRID = "grid gap-5 sm:grid-cols-2 lg:grid-cols-3";
export const FORM_ROW = "grid gap-5 lg:grid-cols-5";
export const FORM_MAIN = "lg:col-span-3";
export const FORM_ASIDE = "lg:col-span-2";

// The column a form or a record sits in, centred on the page. One width for all of them,
// so moving between modules never moves the fields under the cursor.
//
// `w-full` is not decoration and must not be dropped. AppLayout's <main> is a column flex
// container, so a page wrapper is a flex item; `mx-auto` overrides the stretch it would
// otherwise get, which leaves it sized to its own content and merely *capped* by the
// max-width. A page whose content was narrow — a supplier form, a customer record — then
// drew itself two hundred pixels narrower than a page whose content was wide, with both
// files reading max-w-5xl and neither one wrong on the face of it. `w-full` makes the
// stated width the width.
export const PAGE = "mx-auto w-full max-w-5xl";

export function Card({ title, action, children, className = "", bodyClass = "p-5" }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {(title || action) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3.5">
          <h2 className="truncate text-sm font-semibold text-slate-700">{title}</h2>
          {action}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

// A headline figure on a card: the label, the number, and a line saying what it means.
//
// One implementation for the whole portal. There were six, four of them byte-identical, and
// the seventh — the dashboard — had drifted onto the 600 steps of each ramp, which measure
// 3.7:1 against a white card where 12px text needs 4.5:1. Every step below is a 700 or
// darker and clears it; the filled brand card carries white at 5.4:1 and its hint at 4.8:1.
// Colour is checked here once so no screen has to be checked again.
//
// `tone` says what the figure *means*, never merely which series it is:
//   plain    a figure with no verdict on it
//   brand    the one the document is really about — the grand total
//   vat      tax, which is collected rather than earned
//   due      still owed
//   paid     settled, or money that came in
//   muted    carried for reference, not the point of the card
const FIGURE_TONES = {
  plain:   { shell: "border-slate-200 bg-white", value: "text-slate-900", hint: "text-slate-500" },
  brand:   { shell: "border-brand-700 bg-brand-700 shadow-sm", value: "text-white", hint: "text-brand-100" },
  vat:     { shell: "border-amber-200 bg-amber-50/60", value: "text-amber-700", hint: "text-slate-500" },
  due:     { shell: "border-red-200 bg-red-50/60", value: "text-red-600", hint: "text-slate-500" },
  paid:    { shell: "border-emerald-200 bg-emerald-50/60", value: "text-emerald-700", hint: "text-slate-500" },
  muted:   { shell: "border-slate-200 bg-slate-50", value: "text-slate-600", hint: "text-slate-500" },
};
// What the ledger has always called a settled figure. Same thing, and the two names are both
// in use across the screens, so one is the other rather than a sixth colour.
FIGURE_TONES.settled = FIGURE_TONES.paid;

export function Figure({ label, value, hint, aside, tone = "plain", children }) {
  const { shell, value: ink, hint: muted } = FIGURE_TONES[tone] ?? FIGURE_TONES.plain;
  const labelClass = `text-xs font-semibold uppercase tracking-wider ${
    tone === "brand" ? "text-brand-100" : "text-slate-500"
  }`;

  return (
    <div className={`rounded-xl border p-4 ${shell}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={labelClass}>{label}</p>
          {/* Proportional figures, not tabular: at this size tabular digits give every one
              the width of a zero and the number reads loose. Columns are where tabular
              belongs. */}
          <p className={`mt-1.5 text-xl font-bold ${ink}`}>{value}</p>
        </div>
        {/* A second count of the same kind, read against the first and given the far edge
            of the card so the two are plainly two — invoices and the credit notes against
            them, not one number split. Left out entirely when there is nothing to say. */}
        {aside && (
          <div className="shrink-0 text-right">
            <p className={labelClass}>{aside.label}</p>
            <p className={`mt-1.5 text-xl font-bold ${ink}`}>{aside.value}</p>
          </div>
        )}
      </div>
      {hint && <p className={`mt-1 text-xs ${muted}`}>{hint}</p>}
      {children}
    </div>
  );
}

// A small pill for the corner of a card: a count, a percentage, a one-word verdict. Tinted
// rather than filled, so it reads as an aside to the title and never competes with the
// figures below it. Every pair measured against its own tint, where 11px text needs 4.5:1.
const PILL_TONES = {
  brand: "bg-brand-50 text-brand-700 ring-brand-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  red: "bg-red-50 text-red-700 ring-red-100",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

export function Pill({ tone = "slate", children }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${
        PILL_TONES[tone] ?? PILL_TONES.slate
      }`}
    >
      {children}
    </span>
  );
}

const CARD_CHIPS = {
  brand: "bg-brand-50 text-brand-700 ring-brand-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  red: "bg-red-50 text-red-700 ring-red-100",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

// One card per *subject*, holding the two or three figures that subject is made of: an icon
// chip and a title across the top with an optional pill on the right, then a row of figures
// underneath.
//
// This replaced a grid of one-figure tiles, and the difference is not cosmetic. "Sold today"
// is a total, a count of invoices and a count of items; as three tiles the reader had to
// notice they belonged together and assemble them, and the second and third were competing
// for attention with the first at the same weight. On one card the total leads and the other
// two explain it.
export function MetricCard({ icon: CardIcon, title, tone = "brand", badge, href, spread, children }) {
  const Shell = href ? Link : "div";

  return (
    <Shell
      {...(href ? { href } : {})}
      className={`group flex flex-col rounded-2xl bg-white p-4 ring-1 ring-slate-200 transition duration-200 ${
        href ? "hover:-translate-y-0.5 hover:ring-brand-300 hover:shadow-lg hover:shadow-slate-200/70" : ""
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${
            CARD_CHIPS[tone] ?? CARD_CHIPS.brand
          }`}
        >
          <CardIcon className="h-5 w-5" />
        </span>
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{title}</h3>
        {badge}
      </div>

      {/* Each figure takes the width it needs rather than an equal share of the card. Equal
          thirds looked tidier and broke "AED 41,000.00" across two lines to get there, with
          "1" beside it in a column five times wider than its digit. A row that will not fit
          wraps whole figures onto a second line instead.

          `spread` then shares the leftover room out between them, so the gaps come out even
          rather than the figures bunching at the left with the slack all in one piece on the
          right. Worth having where three figures nearly fill the card and wrong where two
          short ones would end up at opposite ends of a wide one, which is why it is asked
          for rather than assumed. */}
      <dl className={`mt-4 flex flex-wrap gap-x-6 gap-y-3 ${spread ? "justify-between" : ""}`}>
        {children}
      </dl>
    </Shell>
  );
}

// Every ink here is a 700 step or slate-900, for the reason the dashboard's tiles already
// documented: against a white card the 600 steps measure 3.7:1 where 12px text needs 4.5:1.
const METRIC_INK = {
  plain: "text-slate-900",
  brand: "text-brand-700",
  emerald: "text-emerald-700",
  red: "text-red-700",
};

export function Metric({ label, value, tone = "plain", hint }) {
  return (
    <div className="shrink-0">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      {/* Proportional figures, not tabular: at this size tabular digits give every one the
          width of a zero and the number reads loose. Columns are where tabular belongs. */}
      <dd
        className={`mt-1 whitespace-nowrap text-xl font-bold leading-none tracking-tight ${
          METRIC_INK[tone] ?? METRIC_INK.plain
        }`}
      >
        {value}
      </dd>
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

// `back` is { href, label } — an icon button beside the title rather than a
// link floating above it, so the way out sits with the thing it leaves.
export function PageHeading({ title, subtitle, action, back }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
        {back && (
          <Link
            href={back.href}
            aria-label={back.label}
            title={back.label}
            className="-ml-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-brand-700 transition hover:bg-brand-50 active:bg-brand-100 sm:h-9 sm:w-9"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
        )}
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

const BUTTON_STYLES = {
  primary:
    "bg-brand-700 text-white hover:bg-brand-800 active:bg-brand-900 focus-visible:outline-brand-700",
  secondary:
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-brand-700",
  danger:
    "border border-red-200 bg-white text-red-700 hover:bg-red-50 active:bg-red-100 focus-visible:outline-red-600",
};

export function Button({ variant = "primary", className = "", type = "button", ...rest }) {
  return (
    <button
      type={type}
      className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60 ${BUTTON_STYLES[variant]} ${className}`}
      {...rest}
    />
  );
}

// Rails hands back an array of messages per field; show the first one.
export function firstError(error) {
  return Array.isArray(error) ? error[0] : error;
}

export function Field({ label, required, error, hint, className = "", htmlFor, children }) {
  const message = firstError(error);
  const fallbackId = useId();

  // Wire the label to its control rather than leaving them merely adjacent:
  // that makes the label clickable, and it's what a screen reader reads out
  // when the field takes focus. Callers that set their own id keep it.
  //
  // `htmlFor` is for when the control is wrapped — an input inside a div that
  // positions a currency prefix, say. Without it the id would land on the
  // wrapper and the label would point at nothing focusable.
  const control = !htmlFor && isValidElement(children) ? children : null;
  const id = htmlFor ?? (control ? control.props.id ?? fallbackId : undefined);
  const noteId = `${id}-note`;

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">
        {control
          ? cloneElement(control, {
              id,
              "aria-invalid": message ? true : undefined,
              "aria-describedby": message || hint ? noteId : undefined,
            })
          : children}
      </div>
      {message ? (
        <p id={noteId} className="mt-1.5 text-sm text-red-600">
          {message}
        </p>
      ) : (
        hint && (
          <p id={noteId} className="mt-1.5 text-xs text-slate-500">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

export function TextInput({ className = "", invalid = false, ...rest }) {
  return (
    <input
      className={`h-11 w-full rounded-lg text-base shadow-sm md:text-sm ${
        invalid
          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
          : "border-slate-300 focus:border-brand-600 focus:ring-brand-600"
      } ${className}`}
      {...rest}
    />
  );
}

// For anything stored in a numeric column. type="number" is what stops letters
// being typed at all; the rest of this closes the holes that leaves behind.
export function NumberInput({ step = "1", min, className = "", invalid = false, onKeyDown, ...rest }) {
  function guardKeys(event) {
    // type=number still accepts exponent notation and a stray plus — no price,
    // count or reference number in this app is ever written as 1e5.
    if ([ "e", "E", "+" ].includes(event.key)) event.preventDefault();
    // A minus only makes sense where negatives do, which is the balances.
    if (event.key === "-" && min !== undefined && Number(min) >= 0) event.preventDefault();

    onKeyDown?.(event);
  }

  return (
    <input
      type="number"
      step={step}
      min={min}
      inputMode={step === "1" ? "numeric" : "decimal"}
      // Scrolling the page with the cursor over a focused number field
      // otherwise changes the value — a figure edited by nobody.
      onWheel={(event) => event.currentTarget.blur()}
      onKeyDown={guardKeys}
      className={`h-11 w-full rounded-lg text-base shadow-sm md:text-sm ${controlTone(invalid)} ${className}`}
      {...rest}
    />
  );
}

// The native picker rather than a bespoke calendar: it's keyboard-accessible,
// localised by the browser, and works on a phone without any of our code.
const CONTROL = "w-full rounded-lg text-base shadow-sm md:text-sm";

function controlTone(invalid) {
  return invalid
    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
    : "border-slate-300 focus:border-brand-600 focus:ring-brand-600";
}

export function Select({ options = [], className = "", invalid = false, ...rest }) {
  return (
    <select className={`h-11 ${CONTROL} ${controlTone(invalid)} ${className}`} {...rest}>
      {options.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

// A tick box with its label beside it, both hitting the same target — a label an inch from
// the box that doesn't toggle it is the commonest reason somebody thinks a form is broken.
// `hint` sits under the label for the sentence that explains what ticking it does.
export function Checkbox({ id, label, hint, checked, onChange, disabled = false }) {
  return (
    <div className={`flex gap-3 ${disabled ? "opacity-50" : ""}`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-brand-700 shadow-sm focus:ring-brand-600 disabled:cursor-not-allowed"
      />
      <label htmlFor={id} className={disabled ? "" : "cursor-pointer"}>
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>}
      </label>
    </div>
  );
}

export function Textarea({ rows = 3, className = "", invalid = false, ...rest }) {
  return (
    <textarea rows={rows} className={`${CONTROL} ${controlTone(invalid)} ${className}`} {...rest} />
  );
}

// Small coloured pill for a record's category — the customer type, for now.
// Every pair measured against its own tint, not against white: the lightest is amber at
// 4.87:1 and the rest clear 5:1, where 11px text needs 4.5:1.
const BADGE_TONES = {
  brand: "bg-brand-50 text-brand-700",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  red: "bg-red-50 text-red-700",
  slate: "bg-slate-100 text-slate-600",
};

export function Badge({ tone = "slate", children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${BADGE_TONES[tone] ?? BADGE_TONES.slate}`}
    >
      {children}
    </span>
  );
}

// A figure on someone's account. Owing is the ordinary case and stays neutral;
// a credit is money the business owes back, so it reads green; a clear account
// is greyed so a screen full of zeros doesn't compete with the real balances.
export function Balance({ value }) {
  const amount = Number(value);
  const owing = Number.isFinite(amount) && amount > 0;
  const credit = Number.isFinite(amount) && amount < 0;

  return (
    <span
      className={`tabular-nums font-semibold ${
        owing ? "text-slate-800" : credit ? "text-emerald-700" : "text-slate-400"
      }`}
    >
      {formatMoney(value)}
    </span>
  );
}

// Tabs that narrow a list to one slice of itself: a full-width card of its own
// above the table, the open one underlined. Each tab carries its own count, so the
// split is visible before a tab is chosen — how many accounts actually owe
// something is usually the question being asked.
//
// tabs: [{ key, label, count }]
export function FilterTabs({ tabs, active, onChange, label = "Filter" }) {
  return (
    <div
      role="group"
      aria-label={label}
      className="rounded-xl border border-slate-200 bg-white px-2 shadow-sm sm:px-4"
    >
      {/* Scrolls rather than wraps: a wrapped second row would break the underline
          away from the strip it belongs to. */}
      <div className="flex items-center gap-1 overflow-x-auto">
        {tabs.map((tab) => {
          const on = tab.key === active;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => onChange(tab.key)}
              aria-pressed={on}
              className={`inline-flex items-center gap-2 whitespace-nowrap border-b-[3px] px-3 py-3.5 text-sm font-semibold transition ${
                on
                  ? "border-brand-700 text-brand-800"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
              <span className={`tabular-nums text-xs ${on ? "text-brand-700" : "text-slate-400"}`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Avatar badge: the photo if there is one, otherwise the "SA" initials square.
// Callers pass the whole user, so adding a photo lights it up everywhere.
export function Avatar({ initials, src, alt, size = "md", className = "" }) {
  const sizes = {
    sm: "h-8 w-8 text-xs",
    md: "h-9 w-9 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-24 w-24 text-2xl",
  };
  const shared = `inline-flex shrink-0 items-center justify-center rounded-lg ${sizes[size]} ${className}`;
  // A file that won't decode — a missing blob, or a local preview of something
  // that isn't really an image — should fall back rather than show a torn page.
  const [broken, setBroken] = useState(false);

  useEffect(() => setBroken(false), [src]);

  if (src && !broken) {
    return (
      <img
        src={src}
        alt={alt ?? ""}
        onError={() => setBroken(true)}
        className={`${shared} bg-slate-100 object-cover`}
        loading="lazy"
      />
    );
  }

  return (
    <span className={`${shared} bg-brand-100 font-bold text-brand-800`}>{initials || "?"}</span>
  );
}

export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      {icon && <div className="mb-3 text-slate-300">{icon}</div>}
      <p className="font-semibold text-slate-700">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
